import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'

import { InboxOutlined } from '@ant-design/icons'
import { Upload, type UploadProps } from 'antd'
import { useGate, useUnit } from 'effector-react'

import { type UploadFileWithId, createMultipleUploadModel } from '../model'

const { Dragger } = Upload

type DraggerUploadFilesProps = Pick<
  UploadProps,
  'id' | 'accept' | 'maxCount'
> & {
  value: UploadFileWithId[] | null
  showRemoveIcon?: boolean
  onChange?: (fileList: UploadFileWithId[]) => void
}

export type DraggerUploadFilesRef = {
  openFileDialog: () => void
}

export const DraggerUploadFiles = forwardRef<
  DraggerUploadFilesRef,
  DraggerUploadFilesProps
>(function DraggerUploadFiles(
  { id, value, accept, maxCount, onChange, showRemoveIcon = true },
  ref,
) {
  const draggerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    openFileDialog() {
      draggerRef.current?.click()
    },
  }))

  const uploadModel = useMemo(() => {
    return createMultipleUploadModel({ maxFiles: maxCount })
  }, [maxCount])
  const [files, initFiles, addFile, removeFile] = useUnit([
    uploadModel.$files,
    uploadModel.filesInit,
    uploadModel.fileAdded,
    uploadModel.fileRemoved,
  ])
  useGate(uploadModel.Gate)

  useEffect(() => {
    if (!value) return
    initFiles(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (files === value) return
    onChange?.(files)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  return maxCount && files?.length < maxCount ? (
    <Dragger
      id={id}
      accept={accept}
      maxCount={maxCount}
      fileList={files}
      beforeUpload={(file) => {
        addFile(file)
        return false
      }}
      onRemove={(file) => {
        removeFile(file)
        return false
      }}
    >
      <div ref={draggerRef}>
        <p>
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Нажмите или перетащите файл в эту область для загрузки
        </p>
      </div>
    </Dragger>
  ) : (
    <Upload
      style={{ display: 'none' }}
      id={id}
      accept={accept}
      maxCount={maxCount}
      fileList={files}
      beforeUpload={(file) => {
        addFile(file)
        return false
      }}
      onRemove={(file) => {
        removeFile(file)
        return false
      }}
      showUploadList={{
        showRemoveIcon: showRemoveIcon,
      }}
    >
      <div ref={draggerRef} />
    </Upload>
  )
})
